export type GradebookColumnKind = 'assessment' | 'h5p' | 'informative' | 'psych';

export interface GradebookColumnSource {
  id: string;
  title: string;
  kind?: GradebookColumnKind | string | null;
  sectionId?: string | null;
  sectionTitle?: string | null;
  sectionSortOrder?: number | null;
  sortOrder?: number | null;
}

export interface GradebookCompletionItem {
  sectionItemId: string;
  sectionId: string;
  sectionTitle?: string | null;
  sectionSortOrder?: number | null;
  sortOrder?: number | null;
  title: string;
  userId: string;
  completed: boolean;
  completedAt?: string | null;
}

export interface GradebookActivityColumn {
  id: string;
  title: string;
  sortOrder: number;
}

export interface GradebookActivitiesGroup {
  type: 'activities';
  sectionId: string;
  sectionTitle: string;
  open: boolean;
  activities: GradebookActivityColumn[];
}

export interface GradebookAssessmentGroup<T extends GradebookColumnSource = GradebookColumnSource> {
  type: 'assessment';
  item: T;
}

export interface GradebookInformativeGroup<T extends GradebookColumnSource = GradebookColumnSource> {
  type: 'informative';
  item: T;
}

export type GradebookColumnGroup<T extends GradebookColumnSource = GradebookColumnSource> =
  | GradebookActivitiesGroup
  | GradebookAssessmentGroup<T>
  | GradebookInformativeGroup<T>;

interface SectionRef {
  id: string;
  title: string;
  sort: number;
}

function compareOrder(a: { sortOrder?: number | null; title: string }, b: { sortOrder?: number | null; title: string }) {
  const left = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const right = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (left !== right) return left - right;
  return a.title.localeCompare(b.title, 'es');
}

function activitiesFromCompletions(rows: GradebookCompletionItem[]): GradebookActivityColumn[] {
  const byId = new Map<string, GradebookActivityColumn>();
  for (const row of rows) {
    const current = byId.get(row.sectionItemId);
    const sortOrder = row.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (!current || sortOrder < current.sortOrder) {
      byId.set(row.sectionItemId, { id: row.sectionItemId, title: row.title, sortOrder });
    }
  }
  return [...byId.values()].sort(compareOrder);
}

/** Cerrado: 1 columna (X/N). Abierto: una por actividad más el total. El resto ocupa 1. */
export function columnSpan(group: GradebookColumnGroup): number {
  if (group.type !== 'activities') return 1;
  return group.open ? group.activities.length + 1 : 1;
}

/**
 * Agrupa el libro por módulo. Las H5P forman un acordeón; las evaluaciones
 * del módulo van después. El examen psicológico se excluye de la tabla.
 */
export function buildColumnGroups<T extends GradebookColumnSource>(
  items: T[],
  completionItems: GradebookCompletionItem[],
  openSections: Set<string>,
): GradebookColumnGroup<T>[] {
  const sections = new Map<string, SectionRef>();

  const remember = (id: string | null | undefined, title: string | null | undefined, sort: number | null | undefined) => {
    if (!id || sections.has(id)) return;
    sections.set(id, { id, title: title ?? '', sort: sort ?? 0 });
  };

  const isPsychItem = (item: T) =>
    item.kind === 'psych' || /examen\s+psicol[oó]gico/i.test(item.title);

  for (const item of items) {
    if (isPsychItem(item)) continue;
    remember(item.sectionId, item.sectionTitle, item.sectionSortOrder);
  }
  for (const row of completionItems) {
    remember(row.sectionId, row.sectionTitle, row.sectionSortOrder);
  }

  const orderedSections = [...sections.values()].sort((a, b) => a.sort - b.sort || a.title.localeCompare(b.title, 'es'));
  const groups: GradebookColumnGroup<T>[] = [];

  for (const section of orderedSections) {
    const h5pItems = items
      .filter((item) => item.kind === 'h5p' && item.sectionId === section.id)
      .map((item) => ({
        id: item.id,
        title: item.title,
        sortOrder: item.sortOrder ?? Number.MAX_SAFE_INTEGER,
      }))
      .sort(compareOrder);
    const activities =
      h5pItems.length > 0
        ? h5pItems
        : activitiesFromCompletions(completionItems.filter((row) => row.sectionId === section.id));

    if (activities.length > 0) {
      groups.push({
        type: 'activities',
        sectionId: section.id,
        sectionTitle: section.title,
        open: openSections.has(section.id),
        activities,
      });
    }

    const rest = items
      .filter(
        (item) =>
          item.sectionId === section.id && item.kind !== 'h5p' && !isPsychItem(item),
      )
      .sort(compareOrder);
    for (const item of rest) {
      groups.push(item.kind === 'informative' ? { type: 'informative', item } : { type: 'assessment', item });
    }
  }

  const loose = items
    .filter((item) => item.kind !== 'h5p' && !isPsychItem(item) && !item.sectionId)
    .sort(compareOrder);
  for (const item of loose) {
    groups.push(item.kind === 'informative' ? { type: 'informative', item } : { type: 'assessment', item });
  }

  return groups;
}

/** Completadas del alumno en ese módulo. No suma otros alumnos ni otros módulos. */
export function countCompleted(
  completionItems: GradebookCompletionItem[],
  sectionId: string,
  userId: string,
): { done: number; total: number } {
  const inSection = completionItems.filter((item) => item.sectionId === sectionId);
  const total = new Set(inSection.map((item) => item.sectionItemId)).size;
  const done = new Set(
    inSection.filter((item) => item.userId === userId && item.completed).map((item) => item.sectionItemId),
  ).size;
  return { done, total };
}
